/**
 * The last part of the New exploration page: the configurations, the prepass,
 * the initial population and the Start button.
 *
 * The five configuration documents (optimization, laser design space, plate
 * design, width measurement, colour measurement) are the page's
 * (setup/useExplorationConfigs.js): the setup cards edit their settings as
 * form fields, and the Configurations card here shows the JSON exactly as it
 * will be stored, read-only.
 *
 * Start is the point of no return: it checks the composed ID is unused,
 * creates the exploration, stores the configs, runs the optional manual or
 * warm start and materialises the first generation and its marking files
 * (idempotent server-side, so a double click cannot create two first
 * iterations). It stays disabled while `startBlockers` names cards that are
 * not complete yet. The actions (ID, Start, Back) render into
 * `actionsContainer` when the page hands one over (its review panel), else
 * below the cards.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Stack,
  Modal,
  Typography,
  Chip,
  Card,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import StartIcon from "@mui/icons-material/Start";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DataObjectRoundedIcon from "@mui/icons-material/DataObjectRounded";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactJson from "react-json-view";
import { projectIdFromSearch } from "../../../helper/projects";
import LoadComponent from "../../../components/LoadComponent";
import PrepassSamplePicker from "../../../components/PrepassSamplePicker";
import ManualSamplesEditor from "../../../components/ManualSamplesEditor";
import DesignPointsErrorAlert from "../../../components/DesignPointsErrorAlert";
import CircularProgress from "@mui/material/CircularProgress";
import { navBottomButtonSx } from "../../../components/NavBarBottom";
import { preprocessJson } from "../../../helper/explorationConfigHelpers";
import { EXPLORATION_LIST_ROUTE, explorationRoute } from "../../../helper/explorationRoutes";
import { manualSpecsFromConfigs, manualStartReady, rowsToPoints } from "../../../helper/manualSamples";
import SetupCard from "./SetupCard";
import { CONFIG_LABELS } from "./useExplorationConfigs";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  overflow: "auto",
  maxHeight: "80vh",
};

const cardBodySx = {
  borderRadius: 2,
  borderColor: "#e7ebf3",
  background: "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
  p: 2,
};

// The cards' numbers continue the page's seven setup cards.
const STEP_CONFIGS = 9;
const STEP_PREPASS = 10;
const STEP_POPULATION = 11;

export default function ExplorationStartSection({
  draft,
  configs,
  substrate = null,
  explorationName,
  startBlockers = [],
  actionsContainer,
}) {
  const navigate = useNavigate();
  // Started from a project's New menu: the exploration is filed there.
  const location = useLocation();
  const projectId = projectIdFromSearch(location.search);
  const [openModal, setOpenModal] = useState(false);
  const [jsonToShow, setJsonToShow] = useState({});
  const {
    explorationConfig = {},
    laserConfig = {},
    plateDesignConfig = {},
    widthMeasurementConfig = {},
    colorMeasurementConfig = {},
    edited: editedConfigs = [],
  } = configs || {};
  const [loading, setLoading] = useState(false);
  const [configName, setConfigName] = useState(null);
  // Surfaced error from any of the sequential setup calls
  // (name check/exploration/configs/initial-step): the flow stops there and
  // the exploration's pages are only opened once everything succeeded.
  const [setupError, setSetupError] = useState(null);

  // Warm start: the chosen file is checked by the server at once
  // (api/exploration/warm-start/check, with the configuration documents this
  // page will post), so a faulty file is refused where it is chosen and
  // Start stays disabled until a valid one is loaded. The points are posted
  // to api/exploration/warm-start during startExplorationClick, before the
  // initial step; a refusal there stops the setup.
  const [warmStartEnabled, setWarmStartEnabled] = useState(false);
  // { name, points } of the chosen file, once it parsed as JSON.
  const [warmStartFile, setWarmStartFile] = useState(null);
  // The server's verdict on it: { status: "checking" } | { status: "ok",
  // pointCount, ignoredKeys } | { status: "refused", error }.
  const [warmStartCheck, setWarmStartCheck] = useState(null);
  const fileInputRef = useRef(null);
  // A file chosen while an older check is in flight must not be judged by
  // that older answer.
  const warmStartCheckSeq = useRef(0);

  // Manual samples: the first population typed in by hand
  // (components/ManualSamplesEditor.js), posted as api/exploration/manual-start
  // between configs/set and the initial step. The backend fills it up to the
  // population size. A refused manual start stops the setup: it must never
  // fall back to a random population.
  const [manualEnabled, setManualEnabled] = useState(false);
  const [manualRows, setManualRows] = useState([]);
  const [manualVary, setManualVary] = useState([]);
  const [manualContinue, setManualContinue] = useState(false);
  const [manualError, setManualError] = useState(null);
  const manualSpecs = useMemo(
    () => manualSpecsFromConfigs(explorationConfig, laserConfig),
    [explorationConfig, laserConfig]
  );
  const manualPopulationSize = Number(
    explorationConfig?.optimization?.population_policy?.start_population_size
  );
  const manualGridSize = plateDesignConfig?.patch_group_grid_size;

  // Prepass: a sample of an earlier exploration on this laser and substrate,
  // marked beneath every patch (perpendicular clusters, marked first). The
  // chosen row is sent as prepass_sample with the create call.
  const [prepassEnabled, setPrepassEnabled] = useState(false);
  const [prepassSample, setPrepassSample] = useState(null);

  // A loaded warm-start file is never dropped behind the user's back
  // (ORC-2250): switching to Random start or Manual samples with a file
  // loaded first asks, and leaving the page warns.
  const [discardPrompt, setDiscardPrompt] = useState(null);   // null | "random" | "manual"
  const dropWarmStartFile = () => {
    warmStartCheckSeq.current += 1;
    setWarmStartFile(null);
    setWarmStartCheck(null);
  };
  const switchToManual = () => {
    setWarmStartEnabled(false);
    dropWarmStartFile();
    setManualEnabled(true);
  };
  const switchToRandom = () => {
    setManualEnabled(false);
    setWarmStartEnabled(false);
    dropWarmStartFile();
  };
  const handleManualToggle = () => {
    if (warmStartFile) {
      setDiscardPrompt("manual");
      return;
    }
    switchToManual();
  };
  const handlePrepassToggle = (enable) => {
    setPrepassEnabled(enable);
    if (!enable) setPrepassSample(null);
  };
  const handleWarmStartToggle = (enable) => {
    if (enable) {
      setManualEnabled(false);
      setWarmStartEnabled(true);
      return;
    }
    if (warmStartFile) {
      setDiscardPrompt("random");
      return;
    }
    switchToRandom();
  };
  const confirmDiscard = () => {
    const target = discardPrompt;
    setDiscardPrompt(null);
    if (target === "manual") switchToManual();
    else if (target === "random") switchToRandom();
  };
  useEffect(() => {
    if (!warmStartFile) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [warmStartFile]);

  // The server's verdict on a warm-start file, against the configuration
  // documents this page holds right now.
  const checkWarmStartFile = async (name, points) => {
    const seq = warmStartCheckSeq.current + 1;
    warmStartCheckSeq.current = seq;
    setWarmStartCheck({ status: "checking" });
    try {
      const response = await axios.post("api/exploration/warm-start/check", {
        explorationConfig,
        laserConfig,
        points,
        fileName: name,
      });
      if (warmStartCheckSeq.current !== seq) return;
      setWarmStartCheck({
        status: "ok",
        pointCount: Number(response.data?.point_count) || 0,
        ignoredKeys: Array.isArray(response.data?.ignored_keys) ? response.data.ignored_keys : [],
      });
    } catch (err) {
      if (warmStartCheckSeq.current !== seq) return;
      const data = err?.response?.data;
      setWarmStartCheck({
        status: "refused",
        error: data && (data.error || data.code) ? data : { error: err?.message || "The file could not be checked." },
      });
    }
  };

  const handleWarmStartFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!e.target) return;
    e.target.value = "";
    if (!file) return;

    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      warmStartCheckSeq.current += 1;
      setWarmStartFile(null);
      setWarmStartCheck({ status: "refused", error: { error: "Invalid JSON file — could not parse." } });
      return;
    }
    setWarmStartFile({ name: file.name, points: parsed });
    await checkWarmStartFile(file.name, parsed);
  };

  // A file checked against one design space must be checked again when the
  // laser, the objective or an edited configuration changes it.
  useEffect(() => {
    if (warmStartFile) {
      checkWarmStartFile(warmStartFile.name, warmStartFile.points);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explorationConfig, laserConfig]);

  const warmStartReady = warmStartFile !== null && warmStartCheck?.status === "ok";
  const warmStartBlocked = warmStartEnabled && !manualEnabled && !warmStartReady;

  const handleOpenModal = (json, config_name) => {
    setJsonToShow(json);
    setConfigName(config_name);
    setOpenModal(true);
  };
  const handleCloseModal = () => setOpenModal(false);

  /**
   * Translate any axios error into a structured `setupError` object the
   * dialog can render. Picks the most informative message available, and
   * preserves the backend's `code` (e.g. "EXPLORATION_NOT_FOUND") so the
   * UI text can adapt later if we ever want to.
   */
  const buildSetupError = (step, err) => {
    const status = err?.response?.status ?? null;
    const data = err?.response?.data ?? null;
    const code = data?.code ?? null;
    // DRF ModelSerializer 400s come back as field-keyed errors, e.g.
    // {"material_name": ["material with this material name already exists."]}.
    // Flatten any field-keyed shape so the actual validation reason shows.
    const flattenDrfErrors = (payload) => {
      if (payload == null) return null;
      if (typeof payload === "string") return payload;
      if (Array.isArray(payload)) return payload.map(flattenDrfErrors).filter(Boolean).join("; ");
      if (typeof payload === "object") {
        const parts = Object.entries(payload)
          .filter(([key]) => !["code"].includes(key))
          .map(([key, value]) => {
            const msg = flattenDrfErrors(value);
            if (!msg) return null;
            // Non-field errors come back under "non_field_errors"/"detail".
            return ["non_field_errors", "detail", "error", "message"].includes(key)
              ? msg
              : `${key}: ${msg}`;
          })
          .filter(Boolean);
        return parts.length ? parts.join("; ") : null;
      }
      return String(payload);
    };
    const detail =
      data?.error ||
      data?.detail ||
      data?.message ||
      flattenDrfErrors(data) ||
      err?.message ||
      "Unknown error.";
    return { step, status, code, message: String(detail) };
  };

  const startExplorationClick = async () => {
    setSetupError(null);
    setLoading(true);

    // Every step is awaited and a failure (e.g. duplicate exploration name
    // -> 400, configs/set -> 500) stops here with the error shown; the
    // exploration's pages are only opened once the create call succeeded.
    try {
      // Substrates are created/managed on the Substrates page, not here —
      // by this point one must simply have been selected.
      const material_id = draft.materialId;
      if (!material_id || material_id.length === 0) {
        setSetupError({
          step: "Select substrate",
          status: null,
          code: "NO_SUBSTRATE",
          message:
            "No substrate is selected. Choose one in the Substrate card " +
            "(new substrates are created on the Substrates page), then start again.",
        });
        return;
      }

      // A prepass that is switched on but not picked is refused rather than
      // silently started without one.
      if (prepassEnabled && !prepassSample) {
        setSetupError({
          step: "Prepass",
          status: null,
          code: "NO_PREPASS_SAMPLE",
          message: "Pick the prepass sample, or switch to No prepass.",
        });
        return;
      }

      // The typed samples are checked here, before anything is created.
      if (manualEnabled) {
        const readiness = manualStartReady({
          rows: manualRows,
          specs: manualSpecs,
          vary: manualVary,
          populationSize: manualPopulationSize,
          gridSize: manualGridSize,
        });
        if (!readiness.ok) {
          setSetupError({
            step: "Manual samples",
            status: null,
            code: "MANUAL_SAMPLES_INVALID",
            message: readiness.reason,
          });
          return;
        }
      }

      // Another exploration with this ID is refused before anything is created.
      try {
        const existing = await axios.get(
          "api/color/explorer/exploration/?name=" + encodeURIComponent(explorationName)
        );
        if (Array.isArray(existing.data) && existing.data.length > 0) {
          setSetupError({
            step: "Exploration name",
            status: null,
            code: "NAME_TAKEN",
            message:
              `An exploration named ${explorationName} already exists. ` +
              "Choose another tag in the Exploration name card.",
          });
          return;
        }
      } catch (err) {
        console.error("Exploration name check failed:", err);
        setSetupError(buildSetupError("Exploration name", err));
        return;
      }

      const exploration_data = {
        name: explorationName,
        iteration_count: 10,
        colorimetric_calibration: draft.cameraCalibrationId,
        auth_user: localStorage.getItem("userId"),
        laser_device: draft.laserId,
        material: material_id,
        exploration_objective: draft.objectiveId,
        description: draft.description,
        status: "PENDING",
        ...(prepassEnabled && prepassSample ? { prepass_sample: prepassSample.sample_id } : {}),
        ...(projectId ? { project: projectId } : {}),
      };
      let createdExplorationId = null;
      try {
        const res = await axios.post("api/color/explorer/exploration/", exploration_data);
        createdExplorationId = res?.data?.id ?? null;
        if (!createdExplorationId) {
          throw new Error("Exploration was created but no id was returned.");
        }
      } catch (err) {
        console.error("Exploration creation failed:", err);
        setSetupError(buildSetupError("Create exploration", err));
        return;
      }

      // The color measurement config is immutable (built from the selected
      // calibration's columns) and is deliberately not sent.
      const updateDataConfigs = {
        explorationId: createdExplorationId,
        explorationConfig,
        laserConfig,
        plateDesignConfig,
        plateOffsetMm: draft.plateOffset,
        widthMeasurementConfig,
        colorCalibrationId: draft.cameraCalibrationId,
      };
      try {
        await axios.post("api/all/configs/set", updateDataConfigs);
      } catch (err) {
        console.error("Persisting exploration configs failed:", err);
        setSetupError(buildSetupError("Persist exploration configs", err));
        return;
      }

      if (manualEnabled) {
        setManualError(null);
        try {
          await axios.post("api/exploration/manual-start", {
            explorationId: createdExplorationId,
            points: rowsToPoints(manualRows, manualSpecs),
            vary_parameters: manualVary,
            continue_with_optimizer: manualContinue,
          });
        } catch (err) {
          console.error("Manual start failed:", err);
          setManualError(err.response?.data || { error: err.message });
          setSetupError(buildSetupError("Manual samples", err));
          // The exploration row was created but never started: remove it
          // so the corrected samples can be started under the same name.
          try {
            await axios.delete(`api/exploration/delete/${createdExplorationId}/`);
          } catch (deleteErr) {
            console.error("Removing the unstarted exploration failed:", deleteErr);
          }
          return;
        }
      }

      if (!manualEnabled && warmStartEnabled) {
        // Start is disabled until the file passed the check, so this is
        // the stored verdict on the same points. A refusal here (the
        // design space changed underneath, or the server disagrees) stops
        // the setup: it must never fall back to a random population.
        try {
          await axios.post("api/exploration/warm-start", {
            explorationId: createdExplorationId,
            points: warmStartFile.points,
            fileName: warmStartFile.name,
          });
        } catch (err) {
          console.error("Warm start failed:", err);
          setWarmStartCheck({ status: "refused", error: err.response?.data || { error: err.message } });
          setSetupError(buildSetupError("Warm start", err));
          // The exploration row was created but never started: remove it
          // so the corrected file can be started under the same name.
          try {
            await axios.delete(`api/exploration/delete/${createdExplorationId}/`);
          } catch (deleteErr) {
            console.error("Removing the unstarted exploration failed:", deleteErr);
          }
          return;
        }
      }

      const exploration_start_data = {
        explorationId: createdExplorationId,
        iterationNumber: 0,
      };
      try {
        await axios.post("api/exploration/initial/step", exploration_start_data);
      } catch (err) {
        console.error("Initial exploration step failed:", err);
        setSetupError(buildSetupError("Run initial exploration step", err));
        return;
      }

      navigate(explorationRoute(createdExplorationId, "resolution"));
    } finally {
      setLoading(false);
    }
  };

  const configSummary = editedConfigs.length
    ? `Changed: ${editedConfigs.map((key) => CONFIG_LABELS[key] || key).join(", ")}`
    : "Defaults for the chosen preset, laser and calibration";
  const prepassSummary = !prepassEnabled
    ? "No prepass"
    : prepassSample
    ? `Sample ${prepassSample.sample_id}${prepassSample.exploration_name ? ` of ${prepassSample.exploration_name}` : ""}`
    : "Pick the prepass sample";
  const populationSummary = manualEnabled
    ? `Manual samples: ${manualRows.length} typed`
    : warmStartEnabled
    ? warmStartCheck?.status === "ok"
      ? `Warm start: ${warmStartCheck.pointCount} sample point${warmStartCheck.pointCount !== 1 ? "s" : ""}${warmStartFile?.name ? ` from ${warmStartFile.name}` : ""}`
      : warmStartCheck?.status === "checking"
      ? `Warm start: checking ${warmStartFile?.name || "the file"}`
      : warmStartCheck?.status === "refused"
      ? `Warm start: ${warmStartFile?.name || "the file"} was refused`
      : "Warm start: upload a JSON file"
    : "Random start";
  // What Start will do, stated right above the button (ORC-2250).
  const startMode = manualEnabled
    ? `Manual samples, ${manualRows.length} typed`
    : warmStartEnabled
    ? warmStartCheck?.status === "ok"
      ? `Warm start, ${warmStartCheck.pointCount} sample point${warmStartCheck.pointCount !== 1 ? "s" : ""}`
      : "Warm start, no file yet"
    : "Random population";
  const blockers = [...startBlockers, ...(warmStartBlocked ? ["Warm-start file"] : [])];
  const blocked = blockers.length > 0;
  const prepassMissing = prepassEnabled && !prepassSample;
  const populationMissing = (manualEnabled && manualRows.length === 0) || warmStartBlocked;

  const configButton = (label, json, key) => (
    <Button
      key={key}
      variant="outlined"
      size="small"
      startIcon={<DataObjectRoundedIcon />}
      sx={{
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 2,
        borderColor: "rgba(15, 23, 42, 0.16)",
        color: "#0f172a",
        bgcolor: "#ffffff",
        "&:hover": { borderColor: "rgba(76, 126, 255, 0.6)", bgcolor: "#f3f7fd" },
      }}
      onClick={() => handleOpenModal(json, key)}
    >
      {label}
    </Button>
  );

  const actions = (
    <Stack spacing={1.25} sx={{ width: "100%" }}>
      {/* Beside the cards the review panel shows the ID; below them this line does. */}
      {actionsContainer === undefined ? (
      <Typography
        variant="body2"
        data-testid="exploration-id"
        sx={{ fontFamily: "ui-monospace, \"SFMono-Regular\", Menlo, Consolas, monospace", fontSize: "0.78rem", wordBreak: "break-all" }}
        color={explorationName ? "text.primary" : "text.secondary"}
      >
        {`Exploration ID: ${explorationName || "complete the cards above"}`}
      </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" data-testid="start-mode" sx={{ lineHeight: 1.4 }}>
        {`Start mode: ${startMode}`}
      </Typography>
      <Button
        variant="contained"
        fullWidth
        size="large"
        startIcon={<StartIcon />}
        disabled={blocked || loading}
        sx={{ height: 44, textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none" }}
        onClick={startExplorationClick}
        data-testid="start-exploration"
      >
        Start exploration
      </Button>
      {blocked ? (
        <Typography variant="caption" color="text.secondary" data-testid="start-blockers" sx={{ lineHeight: 1.4 }}>
          {`Complete first: ${blockers.join(", ")}`}
        </Typography>
      ) : null}
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        sx={{ ...navBottomButtonSx, alignSelf: "flex-start", px: 1, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
        onClick={() => navigate(EXPLORATION_LIST_ROUTE)}
      >
        Back
      </Button>
    </Stack>
  );

  return (
    <>
      {loading && <LoadComponent message="Setting up exploration..." />}

      <SetupCard
        cardKey="configs"
        step={STEP_CONFIGS}
        title="Configurations"
        description="The JSON documents stored with the exploration, as the cards above compose them"
        summary={configSummary}
        complete
      >
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Read-only: every setting is changed in its card above. These are the documents exactly as
            they will be stored with the exploration.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
            {configButton("Optimization", explorationConfig, "exploration_config")}
            {configButton("Laser", laserConfig, "laser_config")}
            {configButton("Plate design", plateDesignConfig, "plate_config")}
            {configButton("Width measurement", widthMeasurementConfig, "width_measurement_config")}
            {configButton("Color measurement", colorMeasurementConfig, "color_measurement_config")}
          </Stack>
          {substrate ? (
            <Typography variant="body2" color="text.secondary">
              {`Plate ${substrate.length} × ${substrate.width} mm (${substrate.material_name}) at x ${draft.plateOffset.x}, y ${draft.plateOffset.y} mm • header ${draft.markHeader ? "on" : "off"}`}
            </Typography>
          ) : null}
        </Stack>
      </SetupCard>

      <SetupCard
        cardKey="prepass"
        step={STEP_PREPASS}
        title="Prepass"
        description="A sample of an earlier exploration marked beneath every patch"
        summary={prepassSummary}
        complete={!prepassMissing}
        missing={prepassMissing}
      >
        <Stack spacing={2} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            Mark a sample of an earlier exploration on this laser and substrate beneath every patch,
            with its lines perpendicular to the patch lines.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label="No prepass"
              variant={prepassEnabled ? "outlined" : "filled"}
              color={prepassEnabled ? "default" : "primary"}
              onClick={() => handlePrepassToggle(false)}
              sx={{ cursor: "pointer" }}
            />
            <Chip
              label="Prepass sample"
              variant={prepassEnabled ? "filled" : "outlined"}
              color={prepassEnabled ? "primary" : "default"}
              onClick={() => handlePrepassToggle(true)}
              sx={{ cursor: "pointer" }}
            />
          </Stack>
          {prepassEnabled && (
            <Card variant="outlined" sx={{ ...cardBodySx, maxWidth: 640, width: "100%" }}>
              <PrepassSamplePicker
                laserId={draft.laserId}
                materialId={draft.materialId}
                value={prepassSample}
                onChange={setPrepassSample}
              />
            </Card>
          )}
        </Stack>
      </SetupCard>

      <SetupCard
        cardKey="population"
        step={STEP_POPULATION}
        title="Initial population"
        description="How the first set of sample points is generated"
        summary={populationSummary}
        complete={!populationMissing}
        missing={populationMissing}
      >
        <Stack spacing={2} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            Choose how the first set of sample points is generated when the exploration starts.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label="Random start"
              variant={warmStartEnabled || manualEnabled ? "outlined" : "filled"}
              color={warmStartEnabled || manualEnabled ? "default" : "primary"}
              onClick={() => handleWarmStartToggle(false)}
              sx={{ cursor: "pointer" }}
            />
            <Chip
              label="Warm start"
              variant={warmStartEnabled ? "filled" : "outlined"}
              color={warmStartEnabled ? "primary" : "default"}
              onClick={() => handleWarmStartToggle(true)}
              sx={{ cursor: "pointer" }}
            />
            <Chip
              label="Manual samples"
              variant={manualEnabled ? "filled" : "outlined"}
              color={manualEnabled ? "primary" : "default"}
              onClick={handleManualToggle}
              sx={{ cursor: "pointer" }}
            />
          </Stack>

          {manualEnabled && (
            <Card variant="outlined" sx={{ ...cardBodySx, maxWidth: 1000, width: "100%" }}>
              <ManualSamplesEditor
                specs={manualSpecs}
                rows={manualRows}
                onRowsChange={setManualRows}
                vary={manualVary}
                onVaryChange={setManualVary}
                continueWithOptimizer={manualContinue}
                onContinueChange={setManualContinue}
                populationSize={manualPopulationSize}
                gridSize={manualGridSize}
                serverError={manualError}
              />
            </Card>
          )}

          {warmStartEnabled && (
            <Card variant="outlined" sx={{ ...cardBodySx, maxWidth: 480, width: "100%" }}>
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  Upload a JSON file containing the design-space sample points. Accepted formats:
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, "& li": { fontSize: "0.8rem", color: "text.secondary" } }}
                >
                  <li>
                    Direct list: <code>{"[{ \"power\": 40, … }]"}</code>
                  </li>
                  <li>
                    Export format from another exploration (<code>sample_points[].design_space</code>)
                  </li>
                </Box>

                {warmStartCheck?.status === "checking" ? (
                  <Alert
                    severity="info"
                    icon={<CircularProgress size={16} thickness={4} />}
                    sx={{ py: 0.5, alignItems: "center" }}
                  >
                    Checking {warmStartFile?.name}…
                  </Alert>
                ) : null}
                {warmStartCheck?.status === "ok" ? (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    {warmStartCheck.pointCount} sample point{warmStartCheck.pointCount !== 1 ? "s" : ""} loaded
                    {warmStartFile?.name ? ` from ${warmStartFile.name}` : ""}
                    {warmStartCheck.ignoredKeys.length > 0
                      ? `; keys outside the design space ignored: ${warmStartCheck.ignoredKeys.join(", ")}`
                      : ""}
                  </Alert>
                ) : null}
                {warmStartCheck?.status === "refused" ? (
                  <DesignPointsErrorAlert error={warmStartCheck.error} fallback="The warm-start file was refused." />
                ) : null}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  style={{ display: "none" }}
                  onChange={handleWarmStartFileChange}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ alignSelf: "flex-start", textTransform: "none" }}
                >
                  {warmStartFile ? "Replace file" : "Upload JSON file"}
                </Button>
              </Stack>
            </Card>
          )}
        </Stack>
      </SetupCard>

      {actionsContainer === undefined ? (
        <Box sx={{ pt: 1, maxWidth: 420 }}>{actions}</Box>
      ) : actionsContainer ? (
        createPortal(actions, actionsContainer)
      ) : null}

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 1 }}>
            {`${CONFIG_LABELS[configName] || "Configuration"} — read-only`}
          </Typography>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {configName === "color_measurement_config"
              ? "Built from the selected camera calibration. Run a new calibration to change these values."
              : "Change these values in the setup cards above; this is the document as it will be stored."}
          </Alert>
          <ReactJson
            src={preprocessJson(jsonToShow)}
            enableClipboard
            displayDataTypes={false}
            collapsed={2}
          />

          <Button
            variant="contained"
            onClick={handleCloseModal}
            style={{ marginTop: "10px" }}
          >
            Close
          </Button>
        </Box>
      </Modal>
      <Dialog open={discardPrompt !== null} onClose={() => setDiscardPrompt(null)} aria-labelledby="discard-warm-start-title">
        <DialogTitle id="discard-warm-start-title">Discard the warm-start file?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`${warmStartFile?.name || "The file"} is loaded for the warm start. ${
              discardPrompt === "manual" ? "Manual samples" : "Random start"
            } drops it.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardPrompt(null)}>Keep file</Button>
          <Button onClick={confirmDiscard} color="error" variant="contained" disableElevation>
            Discard file
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(setupError)}
        onClose={() => setSetupError(null)}
        aria-labelledby="setup-error-title"
        aria-describedby="setup-error-description"
      >
        <DialogTitle id="setup-error-title">Could not start exploration</DialogTitle>
        <DialogContent>
          <DialogContentText id="setup-error-description" component="div">
            <Typography variant="body2" sx={{ mb: 1 }}>
              {setupError?.step
                ? `Step "${setupError.step}" failed`
                : "Setup failed"}
              {setupError?.status ? ` with HTTP ${setupError.status}` : ""}.
              The exploration was not started; nothing else will run until
              this is resolved.
            </Typography>
            {setupError?.message ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", mt: 1 }}
              >
                {setupError.status ? `HTTP ${setupError.status}: ` : ""}
                {setupError.message}
                {setupError.code ? ` (${setupError.code})` : ""}
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ mt: 2 }}>
              Fix the underlying issue and click "Start exploration" again. If
              the problem persists, reload the page — a stale exploration id
              from a previous session has already been cleared.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupError(null)} variant="contained" sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
